import { IComponent, ICustomEventData } from '@modules/src/interfaces/IComponents'
import { ContainersNames } from '../../containers/ContainersNames'
import { AssetType } from '@modules/src/enums/AssetType'
import { Orientation } from '@modules/src/enums/Orientation'
import { ComponentEvent } from '@modules/src/enums/ComponentEvent'
import GameSignals from '@/signals/GameSignals'
import Facade from '@/Facade'
import { ModuleSignals } from '@modules/src/signals/ModuleSignals'
import { DeviceUtils } from '@modules/src/utils/DeviceUtils'
import gsap from 'gsap'
import { isFreeSpins } from '@/managers/ui/BackgroundManager'
import { FREE_SPINS_COUNTER_STYLE } from '@/containers/common/TextStyles'
import * as PIXI from 'pixi.js'
import { ScalingTextField } from '@/containers/common/ScalingTextField'

//## Mobile, Landscape check.
const FREE_SPINS_COUNTER_LANDSCAPE_Y = DeviceUtils.isMobileDevice() ? 800 : 333.5

const showCounter = (element: any) => {
    element.alpha = 1
    element.visible = true
}

const hideCounter = (element: any) => {
    element.alpha = 0
    element.visible = false
}

const isPendingUnacceptedPromo = (): boolean => {
    if (Facade.model?.isPromoSpinsRound?.()) {
        return false
    }

    const details = Facade.model?.spinData?.promo_details
    if (!details || Array.isArray(details) || typeof details !== 'object') {
        return false
    }

    const type = String(details.type || '').toLowerCase()
    return type === 'promospins' || type === 'promospin' || type.startsWith('promo')
}

//## Corner case: During Freespins - Free Spins Retrigger + Hold and Win 
let isFreeSpinsRetriggerIntro = false

const latchFeatureIntroKind = (data: ICustomEventData): void => {
    const payload: any = (data as any)?.signalData
    isFreeSpinsRetriggerIntro = Boolean(payload?.retrigger) && String(payload?.kind || '') === 'freespins'
}

const shouldShowFreeSpinsUi = (): boolean => {
    if (Boolean(Facade.model?.spinState?.isHoldAndWinActive)) {
        return false
    }

    const isLiveFreeSpins = Boolean(Facade.model?.roundState?.isFreeSpins || isFreeSpins())
    if (isLiveFreeSpins) {
        return true
    }

    // Accepted PFS stores remaining promo spins on freebet.amount_left. After a nested
    // FS / H&W returns, that leftover must not bring the FS meter back.
    if (Facade.model?.isPromoSpinsRound?.()) {
        return false
    }

    // Unaccepted PFS must not reuse the FS meter (last FS 5/5 or promo copied onto freebet).
    if (isPendingUnacceptedPromo()) {
        return false
    }

    const freebetLeft = Number(Facade.model?.spinData?.freebet?.amount_left ?? 0)
    return Number.isFinite(freebetLeft) && freebetLeft > 0
}

/**
 * Deferred show scheduled by holdAndWinStop. Tracked so a hide arriving in the same tick can
 * cancel it — otherwise showBaseGameBackground hides the counter and this fires immediately
 * afterwards and puts it back, which is how an empty meter survives into the base game.
 */
let featureStateSyncTimeoutId: number | null = null

const cancelFeatureStateSync = (): void => {
    if (featureStateSyncTimeoutId === null) {
        return
    }

    window.clearTimeout(featureStateSyncTimeoutId)
    featureStateSyncTimeoutId = null
}

const syncCounterForFeatureStates = (element: any): void => {
    cancelFeatureStateSync()

    // Hold&Win stop can arrive before model flags settle on the same tick.
    featureStateSyncTimeoutId = window.setTimeout(() => {
        featureStateSyncTimeoutId = null

        if (shouldShowFreeSpinsUi()) {
            showCounter(element)
            return
        }

        hideCounter(element)
    }, 0)
}

/** While a FS spin that awarded a retrigger is awaiting the award popup, keep the pre-response
 * meter text. freebet on the spin response already includes the award. */
let frozenRetriggerCounterText: string | null = null

const getPendingRetriggerAward = (): number => {
    if (!Facade.model?.roundState?.isFreeSpins) {
        return 0
    }

    const award = Number(Facade.model?.spinData?.bonus?.freebet_given ?? 0)
    return Number.isFinite(award) && award > 0 ? award : 0
}

const readLiveCounterText = (freebet: { amount_left?: number; total_amount_given?: number }): string | null => {
    const remainingRaw = Number(freebet.amount_left)
    const totalRaw = Number(freebet.total_amount_given)

    if (!Number.isFinite(remainingRaw) || !Number.isFinite(totalRaw)) {
        return null
    }

    const remaining = Math.max(0, remainingRaw)
    const total = Math.max(Math.max(0, totalRaw), remaining)
    const played = total - remaining
    return `${played}/${total}`
    // return `${remaining}/${total}
}

/**
 * Counts the spin now under way, at the moment it starts.
 *
 * freebet.amount_left is only decremented by the server in the spin response, so a meter read
 * straight from it does not move until stopSpin. Adding the in-flight spin here makes it tick
 * on start instead; the stopSpin update then recomputes the same number, so nothing jumps.
 */
const advanceCounterForSpinStart = (data: ICustomEventData): void => {
    if (!Facade.model?.roundState?.isFreeSpins || !shouldShowFreeSpinsUi()) {
        return
    }

    const freebet = Facade.model?.spinData?.freebet
    const remainingRaw = Number(freebet?.amount_left)
    const totalRaw = Number(freebet?.total_amount_given)

    if (!Number.isFinite(remainingRaw) || !Number.isFinite(totalRaw)) {
        return
    }

    const remaining = Math.max(0, remainingRaw)
    const total = Math.max(Math.max(0, totalRaw), remaining)
    // Never overshoot the total — the last spin shows total/total, not total+1.
    const played = Math.min(total, total - remaining + 1)

    const element = data.component.componentElement
    element.visible = true
    element.alpha = 1
    element.text = `${played}/${total}`
}

const captureCounterTextBeforeSpin = (data: ICustomEventData): void => {
    if (!Facade.model?.roundState?.isFreeSpins) {
        frozenRetriggerCounterText = null
        return
    }

    const element = data.component.componentElement
    const fromElement = String(element?.text || '').trim()
    if (fromElement.includes('/')) {
        frozenRetriggerCounterText = fromElement
        return
    }

    const freebet = Facade.model?.spinData?.freebet
    frozenRetriggerCounterText = freebet ? readLiveCounterText(freebet) : null
}

const updateFreeSpinsCounterValue = (
    data: ICustomEventData,
    options?: { commitRetrigger?: boolean; allowPendingHoldAndWin?: boolean }
): void => {
    const element = data.component.componentElement

    //## Second gate on the same rule as the container's — without the bypass the box shows on the
    //## retrigger path but the value inside stays hidden.
    if (!options?.allowPendingHoldAndWin && !shouldShowFreeSpinsUi()) {
        element.visible = false
        element.alpha = 0
        return
    }

    if (options?.commitRetrigger) {
        frozenRetriggerCounterText = null
    }

    const freebet = data.signalData?.freebet || Facade.model?.spinData?.freebet
    if (!freebet) {
        hideCounter(element)
        return
    }

    // After the server response, freebet already includes the retrigger. Hold the pre-spin
    // text until the award popup commits (showFreeSpinsRetrigger).
    const holdFrozen = !options?.commitRetrigger && getPendingRetriggerAward() > 0 && frozenRetriggerCounterText != null

    const text = holdFrozen ? frozenRetriggerCounterText : readLiveCounterText(freebet)
    if (!text) {
        hideCounter(element)
        return
    }

    element.visible = true
    element.alpha = 1
    element.text = text
}

export const FREE_SPINS_COUNTER_COMPONENT: IComponent = {
    name: ContainersNames.FREE_SPINS_COUNTER_CONTAINER,
    type: AssetType.CONTAINER,
    visible: false,
    events: [
        {
            //## Hide counter for feature intro and max exposure popups.
            onGameSignalsTriggered: [(GameSignals as any).featureIntroRequested, (GameSignals as any).showMaxExposure],
            event: ComponentEvent.CUSTOM_NEW,
            callback: function (data: ICustomEventData) {
                latchFeatureIntroKind(data)
                const element = data.component.componentElement
                gsap.killTweensOf(element)
                hideCounter(element)
            },
        },
        {
            onGameSignalsTriggered: [(GameSignals as any).featureIntroContinue],
            event: ComponentEvent.CUSTOM_NEW,
            callback: function (data: ICustomEventData) {
                const element = data.component.componentElement
                gsap.killTweensOf(element)

                if (isFreeSpinsRetriggerIntro && Boolean(Facade.model?.roundState?.isFreeSpins)) {
                    showCounter(element)
                    return
                }

                if (shouldShowFreeSpinsUi()) {
                    showCounter(element)
                    return
                }

                hideCounter(element)
            },
        },
        {
            onGameSignalsTriggered: [
                GameSignals.bonusStartComplete,
                GameSignals.restoreFreeSpins,
                GameSignals.showFreeSpinsRetrigger,
            ],
            event: ComponentEvent.CUSTOM_NEW,
            callback: function (data: ICustomEventData) {
                const element = data.component.componentElement
                gsap.killTweensOf(element)

                if (shouldShowFreeSpinsUi()) {
                    showCounter(element)
                    return
                }

                hideCounter(element)
            },
        },
        {
            onGameSignalsTriggered: [ModuleSignals.gameResized],
            event: ComponentEvent.CUSTOM_NEW,
            callback: function (data: ICustomEventData) {
                const element = data.component.componentElement

                if (!shouldShowFreeSpinsUi()) {
                    gsap.killTweensOf(element)
                    hideCounter(element)
                    return
                }

                if (!element.visible) {
                    return
                }

                gsap.killTweensOf(element)
                showCounter(element)
            },
        },
        {
            onGameSignalsTriggered: [GameSignals.bonusStop],
            event: ComponentEvent.CUSTOM_NEW,
            callback: function (data: ICustomEventData) {
                const element = data.component.componentElement
                gsap.killTweensOf(element)
                cancelFeatureStateSync()

                if (!element.visible) {
                    return
                }

                gsap.to(element, {
                    duration: 0.35,
                    alpha: 0,
                    onComplete: () => {
                        element.visible = false
                    },
                })
            },
        },
        {
            onGameSignalsTriggered: [GameSignals.showBaseGameBackground, GameSignals.completeSplashScreenState],
            event: ComponentEvent.CUSTOM_NEW,
            callback: function (data: ICustomEventData) {
                cancelFeatureStateSync()
                hideCounter(data.component.componentElement)
            },
        },
        {
            onGameSignalsTriggered: [(GameSignals as any).promoFreeSpins],
            event: ComponentEvent.CUSTOM_NEW,
            callback: function (data: ICustomEventData) {
                if (shouldShowFreeSpinsUi()) {
                    return
                }

                hideCounter(data.component.componentElement)
            },
        },
        {
            onGameSignalsTriggered: [(GameSignals as any).holdAndWinTransitionStart],
            event: ComponentEvent.CUSTOM_NEW,
            callback: function (data: ICustomEventData) {
                cancelFeatureStateSync()
                hideCounter(data.component.componentElement)
            },
        },
        {
            onGameSignalsTriggered: [(GameSignals as any).holdAndWinStartSpin],
            event: ComponentEvent.CUSTOM_NEW,
            callback: function (data: ICustomEventData) {
                cancelFeatureStateSync()
                hideCounter(data.component.componentElement)
            },
        },
        {
            onGameSignalsTriggered: [(GameSignals as any).holdAndWinStop],
            event: ComponentEvent.CUSTOM_NEW,
            callback: function (data: ICustomEventData) {
                const element = data.component.componentElement
                gsap.killTweensOf(element)
                syncCounterForFeatureStates(element)
            },
        },
    ],
    position: [
        {
            orientation: Orientation.LANDSCAPE,
            x: 1620,
            y: FREE_SPINS_COUNTER_LANDSCAPE_Y,
        },
        {
            orientation: Orientation.PORTRAIT,
            x: 789,
            y: 475.5,
        },
    ],
    scale: [
        {
            orientation: Orientation.LANDSCAPE,
            x: 0.8,
            y: 0.8,
        },
        {
            orientation: Orientation.PORTRAIT,
            x: 0.8,
            y: 0.8,
        },
    ],
    items: [
        {
            name: 'FS Win Frame Right',
            type: AssetType.SPRITE,
            src: `counter-box_free-spin_frame.png`,
            anchor: 0.5,
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 0,
                    y: 0,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 0,
                    y: 0,
                },
            ],
        },
        {
            name: 'FS Counter Label',
            type: AssetType.SPRITE,
            src: 'counter-box_free-spin_text.png',
            anchor: 0.5,
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 17,
                    y: -56.5,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 20.5,
                    y: -58,
                },
            ],
        },
        {
            name: 'FS Counter Value',
            type: AssetType.CUSTOM,
            anchor: 0.5,
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 180,
                    y: 75,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 180,
                    y: 75,
                },
            ],
            elementFactory: () => new ScalingTextField(0.7, 220, PIXI.BitmapText as any, FREE_SPINS_COUNTER_STYLE),
            events: [
                {
                    // Tick the meter for the spin now starting, then snapshot it — the snapshot
                    // must hold the advanced value so a retrigger award does not roll it back.
                    onGameSignalsTriggered: [GameSignals.startSpin],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        advanceCounterForSpinStart(data)
                        captureCounterTextBeforeSpin(data)
                    },
                },
                {
                    // Apply the retriggered total only after the award popup, not on scatter land.
                    onGameSignalsTriggered: [GameSignals.showFreeSpinsRetrigger],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        updateFreeSpinsCounterValue(data, { commitRetrigger: true })
                    },
                },
                {
                    //## Split out so the retrigger bypass can be applied to this signal alone.
                    onGameSignalsTriggered: [(GameSignals as any).featureIntroContinue],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        const allowPendingHoldAndWin =
                            isFreeSpinsRetriggerIntro && Boolean(Facade.model?.roundState?.isFreeSpins)

                        updateFreeSpinsCounterValue(data, { allowPendingHoldAndWin })
                    },
                },
                {
                    onGameSignalsTriggered: [
                        GameSignals.stopSpin,
                        GameSignals.showFreeSpinsBackground,
                        GameSignals.bonusStartComplete,
                        GameSignals.completeTransitionAnimation,
                        GameSignals.restoreFreeSpins,
                        (GameSignals as any).holdAndWinStop,
                    ],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        updateFreeSpinsCounterValue(data)
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.showBaseGameBackground],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        frozenRetriggerCounterText = null
                        const element = data.component.componentElement
                        element.text = ''
                    },
                },
            ],
        },
    ],
}
